/**
 * Performance monitoring and Web Vitals tracking
 * Tracks Core Web Vitals and sends to analytics endpoint
 */

import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';

export interface PerformanceMetric {
  id: string;
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  url: string;
  userAgent: string;
  timestamp: number;
  // Additional context
  connectionType?: string;
  deviceType?: 'mobile' | 'tablet' | 'desktop';
  viewportSize?: string;
}

export interface PerformanceReport {
  sessionId: string;
  metrics: PerformanceMetric[];
  pageLoadTime: number;
  navigationTiming: PerformanceTiming | null;
  resourceTiming: PerformanceResourceTiming[];
  url: string;
  referrer: string;
  timestamp: number;
}

// Generate session ID
const generateSessionId = (): string => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Get device type based on viewport
const getDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

// Network information interface
interface NavigatorConnection {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

interface ExtendedNavigator extends Navigator {
  connection?: NavigatorConnection;
}

// Get connection type
const getConnectionType = (): string => {
  const connection = (navigator as ExtendedNavigator).connection;
  return connection?.effectiveType || 'unknown';
};

// Session storage for metrics
const sessionId = generateSessionId();
let collectedMetrics: PerformanceMetric[] = [];

// Send metrics to analytics endpoint
const sendMetrics = async (metrics: PerformanceMetric[]): Promise<void> => {
  if (metrics.length === 0) return;

  try {
    const report: PerformanceReport = {
      sessionId,
      metrics,
      pageLoadTime: performance.now(),
      navigationTiming: performance.timing as PerformanceTiming,
      resourceTiming: performance.getEntriesByType('resource') as PerformanceResourceTiming[],
      url: window.location.href,
      referrer: document.referrer,
      timestamp: Date.now()
    };

    // Send to our analytics endpoint (non-blocking) with fallback
    try {
      const response = await fetch('/api/analytics/web-vitals', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(report),
        // Don't block page unload
        keepalive: true
      });

      if (!response.ok) {
        console.warn('Primary web vitals endpoint failed:', response.status);
        // Try fallback endpoint
        await fetch('/api/analytics/web-vitals-simple', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(report),
          keepalive: true
        });
      }
    } catch (primaryError) {
      console.warn('Primary web vitals endpoint error:', primaryError);
      // Try fallback endpoint
      try {
        await fetch('/api/analytics/web-vitals-simple', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(report),
          keepalive: true
        });
      } catch (fallbackError) {
        console.warn('Fallback web vitals endpoint also failed:', fallbackError);
      }
    }
  } catch (error) {
    console.warn('Error sending web vitals:', error);
  }
};

// Batch metrics and send periodically
const batchMetrics = (() => {
  let timeoutId: NodeJS.Timeout | null = null;
  const BATCH_DELAY = 2000; // 2 seconds

  return (metric: PerformanceMetric) => {
    collectedMetrics.push(metric);

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      sendMetrics([...collectedMetrics]);
      collectedMetrics = [];
      timeoutId = null;
    }, BATCH_DELAY);
  };
})();

// Web Vitals metric interface
interface WebVitalsMetric {
  id: string;
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
}

// Generic metric handler
const handleMetric = (metric: WebVitalsMetric) => {
  const performanceMetric: PerformanceMetric = {
    id: metric.id,
    name: metric.name,
    value: metric.value,
    rating: metric.rating,
    delta: metric.delta,
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: Date.now(),
    connectionType: getConnectionType(),
    deviceType: getDeviceType(),
    viewportSize: `${window.innerWidth}x${window.innerHeight}`
  };

  // Batch the metric
  batchMetrics(performanceMetric);

  // Log in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`Web Vitals - ${metric.name}:`, {
      value: metric.value,
      rating: metric.rating,
      id: metric.id
    });
  }
};

// Initialize Web Vitals tracking
export const initWebVitals = (): void => {
  try {
    // Core Web Vitals
    onCLS(handleMetric);  // Cumulative Layout Shift
    onINP(handleMetric);  // Interaction to Next Paint (replacement for FID)
    onLCP(handleMetric);  // Largest Contentful Paint

    // Other important metrics
    onFCP(handleMetric);  // First Contentful Paint
    onTTFB(handleMetric); // Time to First Byte

    // Send any remaining metrics on page unload
    window.addEventListener('beforeunload', () => {
      if (collectedMetrics.length > 0) {
        sendMetrics([...collectedMetrics]);
      }
    });

    // Also send on visibility change (when user switches tabs)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && collectedMetrics.length > 0) {
        sendMetrics([...collectedMetrics]);
        collectedMetrics = [];
      }
    });

  } catch (error) {
    console.warn('Failed to initialize web vitals tracking:', error);
  }
};

// Manual performance tracking utilities
export const markStart = (name: string): void => {
  performance.mark(`${name}-start`);
};

export const markEnd = (name: string): void => {
  performance.mark(`${name}-end`);
  performance.measure(name, `${name}-start`, `${name}-end`);
};

// Track custom metrics
export const trackCustomMetric = (name: string, value: number, context?: Record<string, unknown>): void => {
  const customMetric: PerformanceMetric = {
    id: `custom_${name}_${Date.now()}`,
    name: `custom:${name}`,
    value,
    rating: 'good', // Custom metrics don't have standard ratings
    delta: 0,
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: Date.now(),
    connectionType: getConnectionType(),
    deviceType: getDeviceType(),
    viewportSize: `${window.innerWidth}x${window.innerHeight}`,
    ...context
  };

  batchMetrics(customMetric);
};

// Memory information interface
interface MemoryInfo {
  usedJSHeapSize?: number;
  totalJSHeapSize?: number;
  jsHeapSizeLimit?: number;
}

interface ExtendedPerformance extends Performance {
  memory?: MemoryInfo;
}

// Get current performance snapshot
export const getPerformanceSnapshot = (): {
  navigation: PerformanceNavigationTiming | null;
  paint: PerformancePaintTiming[];
  resources: PerformanceResourceTiming[];
  memory?: MemoryInfo;
} => {
  return {
    navigation: performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming || null,
    paint: performance.getEntriesByType('paint') as PerformancePaintTiming[],
    resources: performance.getEntriesByType('resource') as PerformanceResourceTiming[],
    memory: (performance as ExtendedPerformance).memory
  };
};

// Export session ID for debugging
export const getSessionId = (): string => sessionId;